// Firebase database client with Supabase-like API for seamless migration
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  addDoc,
  QueryConstraint,
  DocumentData,
  WhereFilterOp,
  OrderByDirection,
  Query,
  DocumentReference,
  documentId,
  writeBatch,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  uploadBytesResumable
} from 'firebase/storage';
import { db, storage, auth } from './firebase';

// Helper to check if a string is an ISO date
const isISODate = (val: any): boolean => {
  if (typeof val !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/.test(val);
};

// Helper to sanitize query values (converts ISO strings to Dates for Firestore)
const sanitizeValue = (val: any): any => {
  if (val === undefined) return null;
  if (val instanceof Date) return val;
  if (isISODate(val)) return new Date(val);
  return val;
};

// Recursive sanitizer for objects being sent to Firestore
const sanitizeData = (data: any): any => {
  if (data === undefined) return null;
  if (data === null || typeof data !== 'object') return data;
  if (data instanceof Date) return data;
  if (data instanceof Timestamp) return data;
  
  if (Array.isArray(data)) {
    return data.map(sanitizeData);
  }
  
  const sanitized: any = {};
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      sanitized[key] = sanitizeData(data[key]);
    }
  }
  return sanitized;
};

// Helper to convert Timestamps to dates
const convertTimestamps = (data: any): any => {
  if (!data) return data;
  if (data instanceof Timestamp) {
    return data.toDate().toISOString();
  }
  if (Array.isArray(data)) {
    return data.map(convertTimestamps);
  }
  if (typeof data === 'object') {
    return Object.keys(data).reduce((acc, key) => {
      acc[key] = convertTimestamps(data[key]);
      return acc;
    }, {} as any);
  }
  return data;
};

// Query builder that mimics Supabase's chainable API
class FirebaseQueryBuilder<T = any> {
  private collectionName: string;
  private constraints: QueryConstraint[] = [];
  private selectFields: string[] = [];
  private singleResult = false;
  private countOnly = false;
  private headOnly = false;
  private docId: string | null = null;
  private insertData: any = null;
  private updateData: any = null;
  private isDelete = false;
  private upsertData: any = null;
  private requestedRelations: { alias: string; table: string; fields: string[] }[] = [];

  constructor(collectionName: string) {
    this.collectionName = collectionName;
  }

  select(fields: string = '*', options?: { count?: string; head?: boolean }) {
    if (options?.count === 'exact') {
      this.countOnly = true;
    }
    if (options?.head) {
      this.headOnly = true;
    }
    if (fields !== '*') {
      this.selectFields = fields.split(',').map(f => f.trim()).filter(f => !f.includes('('));
      this.requestedRelations = this.parseSelect(fields);
      // Firebase migration relations

      // Keep existing warning for now but update it
      if (fields.includes('{') || fields.includes('!inner')) {
        console.warn(`Complex select syntax "${fields}" detected but not fully supported in Firebase migration layer.`);
      }
    }
    return this;
  }

  private parseSelect(fields: string): { alias: string; table: string; fields: string[] }[] {
    const relations: { alias: string; table: string; fields: string[] }[] = [];

    // Pattern: alias:table(field1, field2)
    // We also support table(*)
    const relationPattern = /([\w]+):([\w]+)\(([^)]+)\)/g;
    let match;

    while ((match = relationPattern.exec(fields)) !== null) {
      const [, alias, table, requestedFields] = match;
      relations.push({
        alias,
        table,
        fields: requestedFields === '*' ? [] : requestedFields.split(',').map(f => f.trim())
      });
    }

    // Pattern for simple relationships like exchanges(*) or profiles(full_name)
    const simpleRelationPattern = /(?<!:)([\w]+)\(([^)]+)\)/g;
    while ((match = simpleRelationPattern.exec(fields)) !== null) {
      const [, table, requestedFields] = match;
      relations.push({
        alias: table,
        table,
        fields: requestedFields === '*' ? [] : requestedFields.split(',').map(f => f.trim())
      });
    }

    return relations;
  }

  eq(field: string, value: any) {
    // Handle nested field like 'job.company_id' - Firestore doesn't support this directly
    if (field.includes('.')) {
      // For nested queries, we need to handle this differently
      // This is a limitation - for now, skip nested field constraints
      console.warn(`Nested field query "${field}" not fully supported in Firebase migration`);
    } else if (field === 'id') {
      // Special handling for document ID - store the docId for direct lookup
      this.docId = value;
    } else {
      this.constraints.push(where(field, '==', sanitizeValue(value)));
    }
    return this;
  }

  neq(field: string, value: any) {
    if (field === 'id') {
      this.constraints.push(where(documentId(), '!=', value));
    } else {
      this.constraints.push(where(field, '!=', sanitizeValue(value)));
    }
    return this;
  }

  gt(field: string, value: any) {
    this.constraints.push(where(field, '>', sanitizeValue(value)));
    return this;
  }

  gte(field: string, value: any) {
    this.constraints.push(where(field, '>=', sanitizeValue(value)));
    return this;
  }

  lt(field: string, value: any) {
    this.constraints.push(where(field, '<', sanitizeValue(value)));
    return this;
  }

  lte(field: string, value: any) {
    this.constraints.push(where(field, '<=', sanitizeValue(value)));
    return this;
  }

  in(field: string, values: any[]) {
    if (values.length > 0) {
      const sanitizedValues = values.map(v => sanitizeValue(v));
      if (field === 'id') {
        this.constraints.push(where(documentId(), 'in', sanitizedValues));
      } else {
        this.constraints.push(where(field, 'in', sanitizedValues));
      }
    }
    return this;
  }

  contains(field: string, value: any) {
    this.constraints.push(where(field, 'array-contains', value));
    return this;
  }

  order(field: string, options?: { ascending?: boolean }) {
    const direction: OrderByDirection = options?.ascending === false ? 'desc' : 'asc';
    this.constraints.push(orderBy(field, direction));
    return this;
  }

  limit(count: number) {
    this.constraints.push(firestoreLimit(count));
    return this;
  }

  single() {
    this.singleResult = true;
    return this;
  }

  maybeSingle() {
    this.singleResult = true;
    return this;
  }

  insert(data: any | any[]) {
    this.insertData = data;
    return this;
  }

  update(data: any) {
    this.updateData = data;
    return this;
  }

  upsert(data: any | any[]) {
    this.upsertData = data;
    return this;
  }

  delete() {
    this.isDelete = true;
    return this;
  }

  // Execute the query and return results
  async then<TResult1 = { data: T | T[] | null; error: Error | null; count?: number }, TResult2 = never>(
    onfulfilled?: ((value: { data: T | T[] | null; error: Error | null; count?: number }) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ): Promise<TResult1 | TResult2> {
    try {
      let result: any;

      if (this.insertData) {
        result = await this.executeInsert();
      } else if (this.updateData) {
        result = await this.executeUpdate();
      } else if (this.upsertData) {
        result = await this.executeUpsert();
      } else if (this.isDelete) {
        result = await this.executeDelete();
      } else {
        result = await this.executeSelect();
      }

      if (onfulfilled) {
        return onfulfilled(result);
      }
      return result as TResult1;
    } catch (error) {
      if (onrejected) {
        return onrejected(error);
      }
      return { data: null, error: error as Error } as unknown as TResult1;
    }
  }

  private async executeSelect(): Promise<{ data: T | T[] | null; error: Error | null; count?: number }> {
    const colRef = collection(db, this.collectionName);

    let docSnapshots: any[] = [];
    let count: number | undefined;

    // Handle direct document lookup by ID
    if (this.docId) {
      const docRef = doc(db, this.collectionName, this.docId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        docSnapshots = [snap];
      }
    } else {
      if (this.constraints.length === 0 && this.singleResult) {
        return { data: null, error: null };
      }
      const q = query(colRef, ...this.constraints);
      const snapshot = await getDocs(q);
      docSnapshots = snapshot.docs;
      count = snapshot.size;
    }

    if (this.countOnly) {
      return { data: null, error: null, count };
    }

    if (docSnapshots.length === 0) {
      return { data: this.singleResult ? null : [], error: null };
    }

    const docs = docSnapshots.map(snap => ({
      id: snap.id,
      ...convertTimestamps(snap.data())
    })) as any[];

    // Handle Joins (Post-processing)
    if (this.requestedRelations.length > 0) {
      // Firebase migration joins
      for (const rel of this.requestedRelations) {
        try {
          // Identify foreign key candidates based on alias or table name
          // e.g. alias 'job' -> 'job_id', table 'profiles' -> 'profile_id' or 'user_id'
          const fkCandidates = [
            `${rel.alias}_id`,
            `${rel.table.replace(/s$/, '')}_id`,
            'user_id',
            'candidate_id',
            'company_id',
            'job_id'
          ];

          // Find which FK exists in the current collection metadata (or just use simple heuristic)
          // For now, let's just pick the first one that exists in any of the docs
          if (docs.length > 0) {
            // Firebase migration fields
          }
          const fkField = fkCandidates.find(fk => docs.some(d => d[fk]));
          // Firebase migration relation match

          if (!fkField) {
            // Handle reverse join (e.g. applications for a job)
            // This is more complex as it requires a new query. 
            // For now, we only support many-to-one (the common case for profile/job titles)
            continue;
          }

          const ids = Array.from(new Set(docs.map(d => d[fkField!]).filter(Boolean)));
          // Firebase migration unique IDs
          if (ids.length === 0) continue;

          // Fetch related docs in batches of 10 (Firestore 'in' limit)
          const relatedDocsMap: Record<string, any> = {};
          const batchSize = 10;
          for (let i = 0; i < ids.length; i += batchSize) {
            const batchIds = ids.slice(i, i + batchSize);
            const relColRef = collection(db, rel.table);
            const relQuery = query(relColRef, where(documentId(), 'in', batchIds));
            const relSnapshot = await getDocs(relQuery);

            relSnapshot.docs.forEach(s => {
              const data = convertTimestamps(s.data());
              // Filter fields if specified
              if (rel.fields.length > 0) {
                const filtered: any = { id: s.id };
                rel.fields.forEach(f => {
                  if (data[f] !== undefined) filtered[f] = data[f];
                });
                relatedDocsMap[s.id] = filtered;
              } else {
                relatedDocsMap[s.id] = { id: s.id, ...data };
              }
            });
          }

          // Attach to main docs
          docs.forEach(d => {
            const fkVal = d[fkField!];
            if (fkVal && relatedDocsMap[fkVal]) {
              d[rel.alias] = relatedDocsMap[fkVal];
            } else {
              d[rel.alias] = null;
            }
          });
        } catch (err) {
          console.error(`Join failed for ${rel.alias}:`, err);
        }
      }
    }

    if (this.singleResult) {
      return { data: docs[0] || null, error: null };
    }

    return { data: docs, error: null };
  }

  private async executeInsert(): Promise<{ data: T | T[] | null; error: Error | null }> {
    const dataArray = Array.isArray(this.insertData) ? this.insertData : [this.insertData];
    const results: any[] = [];

    for (const item of dataArray) {
      const sanitizedItem = sanitizeData(item);
      const dataWithTimestamp = {
        ...sanitizedItem,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };

      // If there's an id field, use it as document ID
      if (item.id) {
        await setDoc(doc(db, this.collectionName, item.id), dataWithTimestamp);
        results.push({ id: item.id, ...dataWithTimestamp });
      } else {
        const docRef = await addDoc(collection(db, this.collectionName), dataWithTimestamp);
        results.push({ id: docRef.id, ...dataWithTimestamp });
      }
    }

    return {
      data: Array.isArray(this.insertData) ? results : results[0],
      error: null
    };
  }

  private async executeUpdate(): Promise<{ data: T | T[] | null; error: Error | null }> {
    const sanitizedData = sanitizeData(this.updateData);
    const dataWithTimestamp = {
      ...sanitizedData,
      updated_at: serverTimestamp(),
    };

    // Handle direct document update by ID
    if (this.docId) {
      const docRef = doc(db, this.collectionName, this.docId);
      await updateDoc(docRef, dataWithTimestamp);
      return { data: { id: this.docId, ...dataWithTimestamp } as T, error: null };
    }

    // Find documents matching constraints
    const colRef = collection(db, this.collectionName);
    const q = query(colRef, ...this.constraints);
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return { data: [], error: null };
    }

    const batch = writeBatch(db);
    const results: any[] = [];

    snapshot.docs.forEach((docSnap) => {
      batch.update(doc(db, this.collectionName, docSnap.id), dataWithTimestamp);
      results.push({ id: docSnap.id, ...docSnap.data(), ...dataWithTimestamp });
    });

    await batch.commit();

    return { data: results.length === 1 ? results[0] : results, error: null };
  }

  private async executeUpsert(): Promise<{ data: T | T[] | null; error: Error | null }> {
    const dataArray = Array.isArray(this.upsertData) ? this.upsertData : [this.upsertData];
    const results: any[] = [];

    for (const item of dataArray) {
      const sanitizedItem = sanitizeData(item);
      const dataWithTimestamp = {
        ...sanitizedItem,
        updated_at: serverTimestamp(),
      };

      if (item.id) {
        const docRef = doc(db, this.collectionName, item.id);
        const existing = await getDoc(docRef);
        if (!existing.exists()) {
          dataWithTimestamp.created_at = serverTimestamp();
        }
        await setDoc(docRef, dataWithTimestamp, { merge: true });
        results.push({ id: item.id, ...dataWithTimestamp });
      }
    }

    return {
      data: Array.isArray(this.upsertData) ? results : results[0],
      error: null
    };
  }

  private async executeDelete(): Promise<{ data: null; error: Error | null }> {
    // Handle direct document delete by ID
    if (this.docId) {
      const docRef = doc(db, this.collectionName, this.docId);
      await deleteDoc(docRef);
      return { data: null, error: null };
    }

    const colRef = collection(db, this.collectionName);
    const q = query(colRef, ...this.constraints);
    const snapshot = await getDocs(q);

    const batch = writeBatch(db);
    snapshot.docs.forEach(docSnap => {
      batch.delete(doc(db, this.collectionName, docSnap.id));
    });
    await batch.commit();

    return { data: null, error: null };
  }
}

// Storage builder that mimics Supabase storage API
class FirebaseStorageBuilder {
  private bucketName: string;

  constructor(bucketName: string) {
    this.bucketName = bucketName;
  }

  async upload(path: string, file: File | Blob) {
    try {
      const storageRef = ref(storage, `${this.bucketName}/${path}`);
      const snapshot = await uploadBytes(storageRef, file);
      return { data: { path: snapshot.ref.fullPath }, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  async download(path: string) {
    try {
      const storageRef = ref(storage, `${this.bucketName}/${path}`);
      const url = await getDownloadURL(storageRef);
      return { data: { signedUrl: url }, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  async remove(paths: string[]) {
    try {
      for (const path of paths) {
        const storageRef = ref(storage, `${this.bucketName}/${path}`);
        await deleteObject(storageRef);
      }
      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  getPublicUrl(path: string) {
    // Use Firebase Storage bucket from env config
    const bucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
    const storageRef = ref(storage, `${this.bucketName}/${path}`);
    return {
      data: {
        publicUrl: `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(`${this.bucketName}/${path}`)}?alt=media`
      }
    };
  }

  async createSignedUrl(path: string, expiresIn: number) {
    try {
      const storageRef = ref(storage, `${this.bucketName}/${path}`);
      const url = await getDownloadURL(storageRef);
      return { data: { signedUrl: url }, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }
}

// Auth builder that mimics Supabase auth API (for compatibility)
const authBuilder = {
  getSession: async () => {
    const user = auth.currentUser;
    if (user) {
      // Get Firebase ID token for API calls
      const token = await user.getIdToken();
      return {
        data: {
          session: {
            access_token: token,
            user: Object.assign(user, {
              id: user.uid,
              user_metadata: {
                full_name: user.displayName || '',
                name: user.displayName || '',
                avatar_url: user.photoURL || '',
                picture: user.photoURL || '',
              }
            })
          }
        },
        error: null
      };
    }
    return { data: { session: null }, error: null };
  },

  getUser: async () => {
    const user = auth.currentUser;
    if (user) {
      return {
        data: {
          user: Object.assign(user, {
            id: user.uid,
            user_metadata: {
              full_name: user.displayName || '',
              name: user.displayName || '',
              avatar_url: user.photoURL || '',
              picture: user.photoURL || '',
            }
          })
        },
        error: null
      };
    }
    return { data: { user: null }, error: null };
  },

  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const token = await user.getIdToken();
        const wrappedUser = Object.assign(user, {
          id: user.uid,
          user_metadata: {
            full_name: user.displayName || '',
            name: user.displayName || '',
            avatar_url: user.photoURL || '',
            picture: user.photoURL || '',
          }
        });
        callback('SIGNED_IN', { access_token: token, user: wrappedUser });
      } else {
        callback('SIGNED_OUT', null);
      }
    });
    return { data: { subscription: { unsubscribe } } };
  },
};

// Main client that mimics Supabase client API
export const firebase = {
  from: <T = any>(table: string) => new FirebaseQueryBuilder<T>(table),
  storage: {
    from: (bucket: string) => new FirebaseStorageBuilder(bucket),
  },
  auth: authBuilder,

  // RPC function calls (for Edge Functions replacement)
  functions: {
    invoke: async (functionName: string, options?: { body?: any }) => {
      // For now, we'll handle this through Firebase Cloud Functions or HTTP endpoints
      // This is a placeholder - you'll need to set up Cloud Functions separately
      console.warn(`Firebase Cloud Function "${functionName}" called - implement in Firebase`);
      return { data: null, error: new Error('Cloud Functions not yet configured') };
    }
  },

  // Realtime subscriptions
  channel: (channelName: string) => {
    const unsubs: (() => void)[] = [];

    const instance = {
      on: (type: string, filter: any, callback: (payload: any) => void) => {
        // Set up Firestore realtime listener
        const table = filter.table || filter.schema;
        const filterStr = filter.filter;

        if (table) {
          let q: Query = collection(db, table);

          // If a filter is provided (e.g. "user_id=eq.XYZ"), apply it to the Firestore query
          // This is critical for security: global listeners on collections fail if the user
          // doesn't have read access to the entire table.
          if (filterStr) {
            const match = filterStr.match(/^([^=]+)=eq\.(.+)$/);
            if (match) {
              const [, field, value] = match;
              q = query(q, where(field, '==', value));
            }
          }

          const unsubscribe = onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach(change => {
              const payload = {
                eventType: change.type === 'added' ? 'INSERT' : change.type === 'modified' ? 'UPDATE' : 'DELETE',
                new: change.type !== 'removed' ? { id: change.doc.id, ...convertTimestamps(change.doc.data()) } : null,
                old: change.type !== 'added' ? { id: change.doc.id } : null,
                table: table,
              };
              // Simplified event filtering
              if (type === 'postgres_changes' || type === '*') {
                callback(payload);
              }
            });
          }, (err) => {
            console.warn(`Realtime channel subscription error for ${table}:`, err.message);
          });
          unsubs.push(unsubscribe);
        }
        return instance;
      },
      subscribe: () => {
        return {
          unsubscribe: () => {
            unsubs.forEach(fn => fn());
          }
        };
      },
      _unsubscribe: () => {
        unsubs.forEach(fn => fn());
      },
    };

    return instance;
  },

  // Remove a channel subscription
  removeChannel: (channel: any) => {
    if (channel?._unsubscribe) {
      channel._unsubscribe();
    }
  },
};

// Export as "supabase" for compatibility with existing code
export { firebase as supabase };

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).supabase = firebase;
}

// Export raw Firebase instances for direct access when needed
export { db, storage, auth } from './firebase';
export { functions } from './firebase';

// Type exports for compatibility
export type { DocumentData };
